import { createAsyncThunk } from "@reduxjs/toolkit";
import apiCall from "../AuthorizedApi";
import { CreateRolePayload, UpdateRolePayload, AssignRolePayload } from "../../../Data/role.dto";

/**
 * Fetch permission catalog (15 modules / 55 keys)
 * GET /account/studio/permissions/
 */
export const getPermissionsCatalogAPI = createAsyncThunk(
  "role/getPermissionsCatalogAPI",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "GET",
        url: "/account/studio/permissions/",
      });
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message || "Failed to fetch permissions catalog");
    }
  }
);

/**
 * List all studio roles
 * GET /account/studio/roles/
 */
export const getAllRolesAPI = createAsyncThunk(
  "role/getAllRolesAPI",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "GET",
        url: "/account/studio/roles/",
      });
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message || "Failed to fetch studio roles");
    }
  }
);

/**
 * Get single role details by ID
 * GET /account/studio/roles/:id/
 */
export const getRoleByIdAPI = createAsyncThunk(
  "role/getRoleByIdAPI",
  async (id: number | string, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "GET",
        url: `/account/studio/roles/${id}/`,
      });
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message || "Failed to fetch role details");
    }
  }
);

/**
 * Create a new studio role
 * POST /account/studio/roles/
 */
export const createRoleAPI = createAsyncThunk(
  "role/createRoleAPI",
  async (payload: CreateRolePayload, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "POST",
        url: "/account/studio/roles/",
        headers: {
          "Content-Type": "application/json",
        },
        data: payload,
      });
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message || "Failed to create role");
    }
  }
);

/**
 * Update an existing studio role
 * PATCH /account/studio/roles/:id/
 */
export const updateRoleAPI = createAsyncThunk(
  "role/updateRoleAPI",
  async ({ id, data }: { id: number | string; data: UpdateRolePayload }, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "PATCH",
        url: `/account/studio/roles/${id}/`,
        headers: {
          "Content-Type": "application/json",
        },
        data,
      });
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message || "Failed to update role");
    }
  }
);

/**
 * Delete a studio role
 * DELETE /account/studio/roles/:id/?force=true/false
 */
export const deleteRoleAPI = createAsyncThunk(
  "role/deleteRoleAPI",
  async ({ id, force = false }: { id: number | string; force?: boolean }, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "DELETE",
        url: `/account/studio/roles/${id}/?force=${force}`,
      });
      return { id, data: response.data || response };
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message || "Failed to delete role");
    }
  }
);

/**
 * Assign role to team member (auto-creates role=4 sub-user)
 * POST /account/studio/roles/:role_id/assign/
 */
export const assignRoleAPI = createAsyncThunk(
  "role/assignRoleAPI",
  async ({ roleId, teamMemberId }: { roleId: number | string; teamMemberId: number | string }, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "POST",
        url: `/account/studio/roles/${roleId}/assign/`,
        headers: {
          "Content-Type": "application/json",
        },
        data: {
          team_member_id: Number(teamMemberId),
        } as AssignRolePayload,
      });
      return {
        roleId,
        teamMemberId,
        result: response.data || response,
      };
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message || "Failed to assign role to team member");
    }
  }
);

/**
 * Unassign role from a team member or user
 * POST /account/studio/roles/:role_id/unassign/
 */
export const unassignRoleAPI = createAsyncThunk(
  "role/unassignRoleAPI",
  async (
    {
      roleId,
      teamMemberId,
      userId,
    }: {
      roleId: number | string;
      teamMemberId?: number | string;
      userId?: number | string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall({
        method: "POST",
        url: `/account/studio/roles/${roleId}/unassign/`,
        headers: {
          "Content-Type": "application/json",
        },
        data: {
          team_member_id: teamMemberId ? Number(teamMemberId) : undefined,
          user_id: userId ? Number(userId) : undefined,
        },
      });
      return {
        roleId,
        teamMemberId,
        userId,
        result: response.data || response,
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || error?.message || "Failed to unassign role"
      );
    }
  }
);

/**
 * Get effective permissions for currently logged-in user
 * GET /account/user/me/permissions/
 */
export const getMyPermissionsAPI = createAsyncThunk(
  "role/getMyPermissionsAPI",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall({
        method: "GET",
        url: "/account/user/me/permissions/",
      });
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message || "Failed to fetch user permissions");
    }
  }
);
